/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useRef, useState, ChangeEvent } from "react";
import { Status } from "@nordcloud/gnui/dist/components/input/types";
import styled from "styled-components";
import { Input, Heading, theme, Spacer } from "@nordcloud/gnui";
import {
  RoleBindingByUser,
  RoleBindingsByUsersSortFields,
  SortOrder,
  RbacRoleBindingType,
} from "~/generated/graphql";
import { Sidebar } from "~/components";
import { LoaderWrap } from "~/components/Loader";
import { useDisclosure } from "~/hooks";
import { isNotNil } from "~/tools";
import { useGetRoleBindingsByUsers } from "~/views/permissions/Users/hooks/useGetRoleBindingsByUsers/useGetRoleBindingsByUsers";

type Props = {
  id: string;
  placeholder: string;
  sidebarTitle: string;
  inputValue: string;
  isLoading: boolean;
  onSelect: (data: string) => void;
  isError?: Status;
};

export function SelectUsersSidebar({
  id,
  placeholder,
  sidebarTitle,
  inputValue,
  isLoading,
  onSelect,
  isError,
}: Props) {
  const [filteredOptions, setFilteredOptions] = useState<RoleBindingByUser[]>(
    []
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const { isOpen, open, close } = useDisclosure();
  // TODO: fetch all users
  const { data, loading } = useGetRoleBindingsByUsers({
    page: "1",
    limit: "100",
    sort: {
      field: RoleBindingsByUsersSortFields.Subject,
      order: SortOrder.Asc,
    },
  });

  const users = data.filter(isNotNil).filter((roleBinding) => {
    return roleBinding.type === RbacRoleBindingType.User;
  });

  useEffect(() => {
    setFilteredOptions(users);
  }, [isOpen]);

  const filterBySearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setFilteredOptions(
      users.filter((user) => user.subject.toLowerCase().includes(searchValue))
    );
  };

  return (
    <>
      <Input
        id={id}
        placeholder={placeholder}
        value={inputValue}
        icon="sidebar"
        disabled={isLoading}
        onClick={open}
        onClear={() => {
          onSelect("");
        }}
        status={isError}
        showClearButton
        readOnly
      />
      <Sidebar title={sidebarTitle} isOpen={isOpen} onClick={close}>
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search"
          onChange={filterBySearch}
        />
        <Spacer height={theme.spacing.spacing05} />
        <LoaderWrap loading={loading} inContent>
          {filteredOptions.map((roleBindinByUser, index) => {
            return (
              <SidebarItemContainer
                key={index}
                onClick={() => {
                  onSelect(roleBindinByUser.subject);
                  close();
                }}
              >
                <Heading level={4}>{roleBindinByUser.subject}</Heading>
              </SidebarItemContainer>
            );
          })}
        </LoaderWrap>
      </Sidebar>
    </>
  );
}

const SidebarItemContainer = styled.div`
  padding: ${theme.spacing.spacing02};
  border-bottom: 1px solid ${theme.color.border.border01};
  text-align: left;

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.background.ui03};
  }
`;
